import { Component, Directive, ElementRef, HostBinding, Input, OnChanges, OnInit, Renderer2, SimpleChanges } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AsyncPipe, NgIf } from '@angular/common';
import { HostListenerService } from '../../../../../services/host-listener.service';

/** Base Directive for tables to automatically include the loading behaviors/ui */
@Directive({
    selector: `app-loading-table`,
    standalone: true,
})
export class AppLoadingTableDirective<T> implements OnInit, OnChanges
{
    @HostBinding('class') readonly hostClass = 'app-loading-table animated animated-delay fadeIn';
    @Input() columns: string[] = [];
    @Input() columnClasses: string[] = [];
    @Input() sortHeaderArrows: { [id: string]: 'before' | 'after'; };

    loadingCells: HTMLSpanElement[] = [];

    constructor(private _renderer: Renderer2, private _el: ElementRef) {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.hasOwnProperty('columns') && this.columns?.length && !this.loadingCells.length) {
            this._buildCells();
        }

        if (changes.hasOwnProperty('columnClasses') && this.columnClasses) {
            this._applyColumnClasses();
        }
    }

    ngOnInit() {
        if (this.columns?.length && !this.loadingCells.length) {
            this._buildCells();
        }
    }

    private _buildCells(): void {
        for (const column of this.columns) {
            const loadingCell = this._renderer.createElement('span');
            this._renderer.appendChild(this._el.nativeElement, loadingCell);
            this._renderer.appendChild(loadingCell, this._renderer.createElement('span'));
            this.loadingCells.push(loadingCell);
        }

        if (this.columnClasses?.length) {
            this._applyColumnClasses();
        }
    }

    private _applyColumnClasses(): void {
        for (const index in this.columns) {
            if (this.loadingCells[index] && this.columns[index] && this.columnClasses[index]) {
                for (const columnClass of this.columnClasses[index].split(' ')) {
                    this._renderer.addClass(this.loadingCells[index], columnClass);
                }
            }
        }
    }
}

@Component({
    selector: 'app-loading-table-state',
    templateUrl: './loading-table-state.component.html',
    styleUrls: ['./loading-table-state.component.scss'],
    standalone: true,
    imports: [
        AsyncPipe,
        NgIf,
        AppLoadingTableDirective,
    ],
})
export class AppLoadingTableStateComponent implements OnChanges
{
    @Input() keys: string[] = [];
    @Input() headerRow: ElementRef;

    private readonly columnClassState$ = new BehaviorSubject<string[]>([]);

    constructor(private _host: HostListenerService) {
    }

    get columnClasses$() {
        return this.columnClassState$.asObservable();
    }

    get mobileMode$() {
        return this._host.isMobileMode();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes && changes.hasOwnProperty('headerRow') && this.headerRow) {
            this.columnClassState$.next(this.getColumnClassesForHeaderRows(this.headerRow));
        }
    }

    private getColumnClassesForHeaderRows(content: ElementRef): string[] {
        const nodes: HTMLElement[] = Array.from(content.nativeElement.childNodes);
        return nodes.map((node) => {
            const classes = [];
            const childNode = node.childNodes[0] as HTMLElement;
            if (childNode?.classList?.contains('mat-sort-header-position-before')) {
                classes.push('mat-sort-header-position-before');
            }
            node.classList?.forEach((className: string) => {
                if (className.match(/mat-column-[^\s"]/)) {
                    classes.push(className);
                }
            });
            return classes.join(' ');
        });
    }
}
